import { useEffect, useState } from "react";
import {
  fetchUsersNeedingCognitoAttributeSync,
  syncCognitoUserAttributes,
} from "services/cognitoAttributeSync";
import { MissingCognitoUser } from "services/cognitoProvisioning";

type LoadingData = { current: number; total: number };

type ReportData = {
  success: string[];
  skipped: string[];
  error: string[];
};

export const useSyncCognitoAttributes = (open: boolean) => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [users, setUsers] = useState<MissingCognitoUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingData, setLoadingData] = useState<LoadingData>({
    current: 0,
    total: 0,
  });
  const [report, setReport] = useState<ReportData>({
    success: [],
    skipped: [],
    error: [],
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    setCompleted(false);
    setReport({ success: [], skipped: [], error: [] });
    setLoadingData({ current: 0, total: 0 });

    const loadUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchUsersNeedingCognitoAttributeSync();
        setUsers(response.users);
      } catch {
        setError("Unable to load users needing Cognito attribute sync.");
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [open]);

  const syncUsers = async () => {
    if (users.length === 0) {
      return;
    }

    setSyncing(true);
    setCompleted(false);
    setError(null);

    const successElements: string[] = [];
    const skippedElements: string[] = [];
    const errorElements: string[] = [];

    setLoadingData({ current: 0, total: users.length });

    for (const user of users) {
      try {
        const result = await syncCognitoUserAttributes(user.user_id);
        if (result.updatedAttributes.length > 0) {
          successElements.push(user.email);
        } else {
          skippedElements.push(user.email);
        }
      } catch {
        errorElements.push(user.email);
      }

      setLoadingData((prev) => ({
        current: prev.current + 1,
        total: prev.total,
      }));
    }

    setReport({
      success: successElements,
      skipped: skippedElements,
      error: errorElements,
    });
    setSyncing(false);
    setCompleted(true);
  };

  const reset = () => {
    setCompleted(false);
    setReport({ success: [], skipped: [], error: [] });
    setLoadingData({ current: 0, total: 0 });
  };

  return {
    loading,
    syncing,
    completed,
    users,
    error,
    loadingData,
    report,
    syncUsers,
    reset,
  };
};
